import { createContext, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useAppLanguage from "../hooks/useAppLanguage";
import { translateSurvey } from "../utils/lang";
import sortOnField from "../utils/sortOnField";
import LanguageContext from "./language";
import UserContext from "./user";

const OsteopataContext = createContext({
  survey: undefined,
  location: undefined,
  error: undefined,
  infoError: undefined,
  isFetching: undefined,
  appLang: undefined,
  languages: undefined,
  labels: undefined,
  navigation: undefined,
  currentRequest: undefined,
  osteopataInfo: undefined,
  setAppLang: () => 0,
  setNavigation: () => 0,
});

export const OsteopataProvider = ({ children }) => {
  const { callApi, logged, userProfile } = useContext(UserContext);
  const { language, defaultLabels, unloggedLanguage, cmsLang } = useContext(LanguageContext);
  const [navigation, setNavigation] = useState();
  const [requests, setRequests] = useState();
  const [currentRequest, setCurrentRequest] = useState();
  const [error, setError] = useState();

  /* Get osteopath app data */
  const { isFetching: isAppFetching, data: survey } = useQuery(
    ["osteopath", JSON.stringify(userProfile)],
    () => callApi(`/osteopath/default-content`),
    { enabled: !!userProfile, onError: (err) => setError(err) }
  );

  /* Get user requests */
  const { isFetching: isRequestsFetching } = useQuery(
    ["requests", JSON.stringify(userProfile)],
    () => callApi(`/osteopath/requests?offset=0&limit=100`),
    {
      enabled: !!userProfile,
      select: (data) =>
        data.list?.sort((a, b) => sortOnField(a, b, (data) => data.creationDate, false)),
      onError: (err) => setError(err),
      onSuccess: (data) => setRequests(data),
    }
  );

  const isFetching = useMemo(
    () => isAppFetching || isRequestsFetching,
    [isAppFetching, isRequestsFetching]
  );

  const { appLang, setAppLang, languages, labels } = useAppLanguage(
    defaultLabels,
    !!survey && language,
    survey,
    logged && (error ? ![403, 404].includes(error.status) : true) ? null : unloggedLanguage
  );

  const translatedSurvey = useMemo(() => {
    if (survey) {
      return translateSurvey(survey, appLang);
    }
  }, [survey, appLang]);

  const { error: infoError, data: osteopataInfo } = useQuery(
    ["info-osteopata", cmsLang],
    () => callApi(`/resource/hr/${window.OSTEOPATA_INFO_MASTER_ID}?locale=${cmsLang}`),
    {
      enabled: !!userProfile && !!window.OSTEOPATA_INFO_MASTER_ID,
    }
  );

  const contextValue = useMemo(
    () => ({
      error,
      infoError,
      isFetching,
      appLang,
      languages,
      labels,
      navigation,
      requests,
      currentRequest,
      osteopataInfo,
      survey: translatedSurvey,
      setAppLang,
      setNavigation,
      setCurrentRequest,
    }),
    [
      error,
      infoError,
      isFetching,
      appLang,
      languages,
      labels,
      navigation,
      requests,
      currentRequest,
      osteopataInfo,
      translatedSurvey,
      setAppLang,
      setNavigation,
      setCurrentRequest,
    ]
  );

  return <OsteopataContext.Provider value={contextValue}>{children}</OsteopataContext.Provider>;
};

export default OsteopataContext;
